'use client'

import { useCallback } from 'react'
import { useTranslations } from 'next-intl'

import { NOTIFICATION_DRAWER_TEST_IDS } from '@common/common-test-ids.const'

import { selectNotifications, selectUnreadNotificationCount } from '@stores/notifications/notifications.selector'
import { useNotificationsStore } from '@stores/notifications/notifications.store'

import { NotificationCard } from './components/NotificationCard'
import { NotificationDrawerFilters } from './components/NotificationDrawerFilters'
import { NotificationDrawerHeader } from './components/NotificationDrawerHeader'
import { useNotificationDrawerFilters } from './hooks/useNotificationDrawerFilters'
import type { NotificationDrawerProps } from './NotificationDrawer.types'

import styles from './NotificationDrawer.module.scss'

export function NotificationDrawer({ onClose, onSettingsClick }: NotificationDrawerProps) {
  const t = useTranslations('notifications')
  const notifications = useNotificationsStore(selectNotifications)
  const markAsRead = useNotificationsStore((state) => state.markAsRead)
  const markAllAsRead = useNotificationsStore((state) => state.markAllAsRead)
  const dismissNotification = useNotificationsStore((state) => state.dismissNotification)
  const clearAll = useNotificationsStore((state) => state.clearAll)

  const unreadCount = useNotificationsStore(selectUnreadNotificationCount)
  const { filter, setFilter, filteredNotifications } = useNotificationDrawerFilters({ notifications })

  const handleAction = useCallback(
    (notificationId: string, actionId: string) => {
      markAsRead(notificationId)

      if (actionId === 'decline') {
        dismissNotification(notificationId)
      }
    },
    [dismissNotification, markAsRead]
  )

  const handleClearAll = useCallback(() => {
    clearAll()
    onClose()
  }, [clearAll, onClose])

  return (
    <div className={styles.drawer} data-testid={NOTIFICATION_DRAWER_TEST_IDS.root}>
      <NotificationDrawerHeader onClose={onClose} onSettingsClick={onSettingsClick} onClearAll={handleClearAll} />

      <NotificationDrawerFilters
        filter={filter}
        unreadCount={unreadCount}
        onFilterChange={setFilter}
        onMarkAllAsRead={markAllAsRead}
      />

      <div className={styles.list}>
        <div className={styles.listInner}>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <NotificationCard key={notification.id} notification={notification} onAction={handleAction} />
            ))
          ) : (
            <p className={styles.empty}>{t('empty')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
